"use client";

import { c as _c } from "react/compiler-runtime";
import { useContext, useMemo } from "react";
import { UserContext } from "@/contexts/user-context";
import { useLocations } from "@/hooks/use-locations";
import { PERMISSIONS, hasPermission } from "@/lib/permissions";
import type { DefaultPolicy, FilterDefinition, FilterOption, LocationDefaultPolicy, TableFilterDefaultContext } from "./types";
type MutableRequest<Request extends object> = Record<string, unknown> & Partial<Request>;
type ResolvedLocationDefaultPolicy = Exclude<LocationDefaultPolicy, "none">;
const EMPTY_VISIBLE_LOCATION_IDS: string[] = [];
function policies(policy: DefaultPolicy | undefined): ResolvedLocationDefaultPolicy[] {
  if (!policy || policy === "none") return [];
  const values = Array.isArray(policy) ? policy : [policy];
  return values.filter((value): value is ResolvedLocationDefaultPolicy => value !== "none");
}
function hasDefinitionValue<Request extends object>(definition: FilterDefinition<Request>, request: Partial<Request>): boolean {
  const values = request as MutableRequest<Request>;
  return definition.requestKeys.some(key => {
    const value = values[key];
    return !(value === undefined || value === null || value === "" || value === false || Array.isArray(value) && value.length === 0);
  });
}
export function resolveLocationDefaultIds(policy: DefaultPolicy | undefined, context: TableFilterDefaultContext): string[] | undefined {
  for (const candidate of policies(policy)) {
    if (candidate === "currentLocation" && context.currentLocationId) {
      return [context.currentLocationId];
    }
    if (candidate === "singleLocation" && context.locationOptions.length === 1 && context.locationOptions[0]?.value) {
      return [context.locationOptions[0].value];
    }
    if ((candidate === "scopedVisibleLocations" || candidate === "visibleLocations") && context.isScopedLocationUser && context.visibleLocationIds.length > 0) {
      return context.visibleLocationIds;
    }
  }
  return undefined;
}
export function locationDefaultIdentity<Request extends object>(definitions: readonly FilterDefinition<Request>[], context: TableFilterDefaultContext): string {
  return JSON.stringify({
    definitions: definitions.filter(definition => definition.control === "location").map(definition => ({
      key: definition.key,
      requestKeys: definition.requestKeys,
      defaultPolicy: definition.defaultPolicy ?? "none"
    })),
    context: {
      currentLocationId: context.currentLocationId ?? null,
      visibleLocationIds: context.visibleLocationIds,
      locationOptionIds: context.locationOptions.map(option => option.value),
      isScopedLocationUser: context.isScopedLocationUser
    }
  });
}
export function applyLocationDefaults<Request extends object>(definitions: readonly FilterDefinition<Request>[], request: Request, context: TableFilterDefaultContext, suppressedDefinitionKeys: ReadonlySet<string> = new Set()): Request {
  if (!context.isScopedLocationUser && definitions.some(definition => definition.bypassesLocationDefaults && hasDefinitionValue(definition, request))) {
    return request;
  }
  return definitions.reduce<Request>((next, definition) => {
    if (definition.control !== "location") return next;
    if (suppressedDefinitionKeys.has(definition.key)) return next;
    if (hasDefinitionValue(definition, next)) return next;
    const ids = resolveLocationDefaultIds(definition.defaultPolicy, context);
    return ids ? definition.writeControl(next, ids) as Request : next;
  }, request);
}
export function locationFilterValues<Request extends object>(definitions: readonly FilterDefinition<Request>[], request: Partial<Request>): Partial<Request> {
  const source = request as MutableRequest<Request>;
  const values: Record<string, unknown> = {};
  definitions.forEach(definition => {
    if (definition.control !== "location") return;
    definition.requestKeys.forEach(key => {
      values[key as string] = source[key];
    });
  });
  return values as Partial<Request>;
}
export function enhanceLocationFilterDefinitions<Request extends object>(definitions: readonly FilterDefinition<Request>[], context: TableFilterDefaultContext): FilterDefinition<Request>[] {
  return definitions.map(definition => {
    if (definition.control !== "location") return definition;
    const options = definition.options?.length ? definition.options : context.locationOptions;
    return {
      ...definition,
      options
    };
  });
}
export function useTableFilterDefaultContext(t0) {
  const $ = _c(12);
  const includeArchivedLocations = t0 === undefined ? false : t0;
  const userContext = useContext(UserContext);
  const currentUser = userContext?.currentUser;
  const currentLocationId = userContext?.currentLocationId ?? undefined;
  const visibleLocationIds = userContext?.visibleLocationIds ?? EMPTY_VISIBLE_LOCATION_IDS;
  let t1;
  if ($[0] !== includeArchivedLocations) {
    t1 = {
      includeArchived: includeArchivedLocations
    };
    $[0] = includeArchivedLocations;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    locations
  } = useLocations(t1);
  let t2;
  if ($[2] !== locations) {
    t2 = Array.isArray(locations) ? locations.map(_temp) : [];
    $[2] = locations;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const locationOptions = t2;
  let t3;
  if ($[4] !== currentUser || $[5] !== visibleLocationIds) {
    t3 = !hasPermission(currentUser, PERMISSIONS.LOCATIONS_SEE_ALL) && visibleLocationIds.length > 0;
    $[4] = currentUser;
    $[5] = visibleLocationIds;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const isScopedLocationUser = t3;
  let t4;
  if ($[7] !== currentLocationId || $[8] !== isScopedLocationUser || $[9] !== locationOptions || $[10] !== visibleLocationIds) {
    t4 = {
      currentLocationId,
      visibleLocationIds,
      locationOptions,
      isScopedLocationUser
    };
    $[7] = currentLocationId;
    $[8] = isScopedLocationUser;
    $[9] = locationOptions;
    $[10] = visibleLocationIds;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
}
function _temp(location) {
  return {
    value: location.id,
    label: location.name
  };
}